import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  Grid,
  Paper,
  Typography,
  Button,
  Menu,
  MenuItem,
  Divider,
  Slide,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: '30px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
  },
  button: {
    marginRight: theme.spacing(1),
    padding: theme.spacing(0, 1),
    borderColor: '#d2dae1',
    borderRadius: '50px',
    color: '#000',
    fontSize: '12px',
    '&:hover': {
      borderColor: '#9fa5ab',
    }
  },
  secondaryButton: {
    width: 'fit-content',
    padding: '5px 16px',
    marginBottom: '30px',
    fontSize: '14px',
    backgroundColor: '#5b8d8b',
    borderColor: '#5b8d8b',
    textTransform: 'none',
    borderRadius: '50px',
    '&:hover': {
      backgroundColor: '#48706f',
    },
    '&.modal': {
      fontSize: '12px',
      borderRadius: '50px',
      marginBottom: 0,
    },
  },
  cardPreview: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(6),
    backgroundColor: '#393b3a',
    borderRadius: '16px',
  },
  cardDetail: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#FFF',
  },
  title: {
    color: '#586065',
  },
  option: {
    display: 'flex',
    margin: theme.spacing(2, 0),
    color: '#586065',
  },
  marker: {
    height: 60,
    width: 80,
    backgroundColor: '#e9edf4',
    borderRadius: 6,
  },
  description: {
    marginLeft: '60px',
  },
  initialView: {
    margin: '0 auto',
    width: '250px',
    color: '#8e9895',
    textAlign: 'center',
    '& h6': {
      marginBottom: theme.spacing(3),
    }
  },
  frontTitle: {
    color: '#558988',
  },
  frontDescription: {
    margin: '40px auto',
    width: '200px',
    fontSize: '1.25rem',
  },
  backTitle: {
    paddingTop: theme.spacing(10),
  },
  cardView: {
    margin: 'auto',
    height: 212,
    width: 295,
    cursor: 'pointer',
    perspective: '1000px',
  },
  noCardFrontView: {
    '& #card_inner': {
      transform: 'rotateY(180deg)',
    },
  },
  cardInner: {
    position: 'relative',
    width: '100%',
    height: '100%',
    textAlign: 'center',
    transition: 'transform 0.6s',
    transformStyle: 'preserve-3d',
    boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)',
  },
  cardBackView: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden',
    transform: 'rotateY(180deg)',
    background: '#FFF',
    borderRadius: 20,
  },
  cardFrontView: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden',
    background: '#ecece3',
    borderRadius: 20,
  }
}));

const Cards = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [cardPreview, setCardPreview] = useState(false);
  const [isFrontView, setIsFrontView] = useState(true);
  const containerRef = useRef(null);

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={classes.header}>
        <Button variant='outlined' className={classes.button} onClick={() => navigate(-1)}>
          <NavigateBeforeIcon />
          Sets
        </Button>
        <Typography>{' / Practice Cards'}</Typography>

        <Button variant='outlined' className={classes.button} sx={{ marginLeft: 'auto' }} onClick={(e) => setAnchorEl(e.currentTarget)}>
          More
          <ArrowDropDownIcon />
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          className={classes.menu}
        >
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/sets/1/celeration'); }}>Daily Chart</MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/sets/1/timing'); }}>Timing Chart</MenuItem>
          <Divider />
          <MenuItem>Set Display Order</MenuItem>
        </Menu>
      </Paper>

      <Grid container spacing={4}>
        <Grid item xs={7}>
          <Paper elevation={0} className={classes.cardPreview} ref={containerRef}>
            {cardPreview ? (
              <Slide direction='left' in={cardPreview} container={containerRef.current}>
                <div className={clsx(classes.cardView, { [classes.noCardFrontView]: !isFrontView })} onClick={() => setIsFrontView(!isFrontView)}>
                  <div id="card_inner" className={classes.cardInner}>
                    <div className={classes.cardFrontView}>
                      <Typography className={classes.frontTitle}>Click to Flip</Typography>
                      <Typography className={classes.frontDescription}>Learns under <strong><i>novel circumstances ___</i></strong></Typography>
                    </div>
                    <div className={classes.cardBackView}>
                      <Typography className={classes.backTitle} variant='h5'>Name</Typography>
                      <Button variant='outlined' className={classes.button} sx={{ position: 'absolute', bottom: 10, right: 0 }}>Move to NF</Button>
                    </div>
                  </div>
                </div>
              </Slide>
            ) : (
              <div className={classes.initialView}>
                <Typography variant='h6'>
                  Learn the NEW card before moving it to NEWLY firmed
                </Typography>
                <Button variant='contained' className={classes.secondaryButton} onClick={() => setCardPreview(true)}>View Card</Button>
              </div>
            )}
          </Paper>
        </Grid>
        <Grid item xs={5}>
          <Paper elevation={0} className={classes.cardDetail}>
            <Typography variant='h5' className={classes.title} sx={{ marginBottom: '30px' }}>Decks</Typography>
            <div className={classes.option}>
              <div className={classes.marker}></div>
              <div className={classes.description}>
                <Typography variant='h5' className={classes.subone}>New</Typography>
              </div>
            </div>
            <div className={classes.option}>
              <div className={classes.marker}></div>
              <div className={classes.description}>
                <Typography variant='h5' className={classes.subone}>Newly Firmed</Typography>
                <Typography className={classes.subtwo}>Building Fluency</Typography>
              </div>
            </div>
            <div className={classes.option}>
              <div className={classes.marker}></div>
              <div className={classes.description}>
                <Typography variant='h5' className={classes.subone}>Firming</Typography>
                <Typography className={classes.subtwo}>SAFMEDS deck</Typography>
              </div>
            </div>
          </Paper>
        </Grid>
      </Grid>
    </div>
  )
};

export default Cards;
